import { useCallback, useEffect, useRef, useState } from "react";

/* ---------------------------------------------------------------
   COURSES — loads the full course/lesson tree from the backend
   (GET education/courses/all/) instead of the old static
   data/courses.js fixture.

   The product has no "module" concept and no completion tracking:
   a course just has lessons attached to it directly, and there's
   no lessons-done/tests progress to show (this platform doesn't
   mark courses as completed). If the backend still nests lessons
   under `course.modules[].lessons` (older/legacy shape), they're
   flattened into a single ordered lesson list per course here so
   the UI never has to know about modules either way.

   Backend contract (see the sample payload this was built against):
     {
       "course": [
         {
           "id": "<uuid>", "title": "...", "description": "...",
           "duration": <minutes>, "created_at": "...",
           "lessons": [
             { "id": "<uuid>", "title": "..." | null, "description": "...", "video_link": "..." | null, "created_at": "..." }
           ]
         }
       ]
     }

   `title` can be null for lessons (content not filled in yet in the
   admin panel), so the UI falls back to "<Lesson> N". The lesson
   `id` returned here is exactly what's passed straight through to
   GET education/lesson/{id}/pdf (see LessonPdfScreen) — there's no
   separate "slug" field in the payload, the id IS what the pdf
   endpoint expects in that path segment.
------------------------------------------------------------------ */

function normalizeLesson(lesson, index, t) {
  return {
    id: lesson.id,
    title: lesson.title || `${t.edu_lesson_fallback} ${index + 1}`,
    description: lesson.description || "",
    // Legacy field (still populated for old YouTube/Vimeo-linked
    // lessons) — kept only as the "own storage" fetch fallback
    // trigger below, never rendered as an iframe src directly here.
    videoLink: lesson.video_link || "",
    // New own-storage flag: true once the lesson has a video_file on
    // the backend. `video_link` alone can't be used to decide this
    // anymore since it's null for lessons migrated to video_file.
    hasVideo: Boolean(lesson.has_video) || Boolean(lesson.video_link),
  };
}

/* Backend may send lessons directly on the course (current shape) or
   still nested under modules (legacy shape) — either way, the app
   only ever deals with one flat lesson list per course. */
function collectRawLessons(course) {
  if (Array.isArray(course.lessons)) return course.lessons;
  const modules = Array.isArray(course.modules) ? course.modules : [];
  return modules.flatMap((m) => (Array.isArray(m.lessons) ? m.lessons : []));
}

function normalizeCourse(course, t) {
  const lessons = collectRawLessons(course).map((l, i) => normalizeLesson(l, i, t));
  return {
    id: course.id,
    title: course.title || "",
    about: course.description || "",
    duration: formatDuration(course.duration, t),
    createdAt: course.created_at || null,
    lessons,
  };
}

/* `duration` comes back in minutes as a plain number (0 when unset).
   Formatted using the existing per-language "min" word from i18n
   rather than hardcoding a unit, so it stays consistent with the
   rest of the education UI. */
function formatDuration(minutes, t) {
  const n = Number(minutes) || 0;
  if (n <= 0) return `0 ${t.edu_min}`;
  if (n < 60) return `${n} ${t.edu_min}`;
  const hours = Math.floor(n / 60);
  const rest = n % 60;
  const hourLabel = `${hours} ${t.edu_hour}`;
  return rest > 0 ? `${hourLabel} ${rest} ${t.edu_min}` : hourLabel;
}

export function useCourses(authFetch, t) {
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [courses, setCourses] = useState([]);
  const reqIdRef = useRef(0);

  const load = useCallback(() => {
    if (!authFetch) return;
    const reqId = ++reqIdRef.current;
    setStatus("loading");
    authFetch("education/courses/all", { method: "GET" })
      .then((res) => {
        if (!res.ok) throw new Error(`courses API ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (reqIdRef.current !== reqId) return; // superseded by a newer call
        const rawCourses = Array.isArray(data?.course) ? data.course : [];
        setCourses(rawCourses.map((c) => normalizeCourse(c, t)));
        setStatus("ready");
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error("[useCourses] failed to load courses:", e);
        if (reqIdRef.current !== reqId) return;
        setStatus("error");
      });
  }, [authFetch, t]);

  useEffect(() => {
    load();
  }, [load]);

  return { status, courses, reload: load };
}
